<template>
  <div
    id="loyalty-coins-generator-view"
    :class="`min-h-screen px-4 md:!px-1.5 py-4 transition-all duration-300 ${
      isMenuOpen ? 'md:ml-64' : 'md:ml-20'
    }`"
  >
    <div v-if="isLoading" class="flex items-center justify-center min-h-[60vh]">
      <Loader />
    </div>

    <div v-else class="max-w-full mx-auto">
      <!-- Header -->
      <header class="mb-8">
        <div class="flex flex-col gap-1">
          <h1 class="text-3xl font-extrabold text-slate-800 tracking-tight">
            {{ $route.params.id ? "Update" : "Create" }} Loyalty Coins Campaign
            <span class="text-amber-600" v-if="$route.params.id">#{{ $route.params.id }}</span>
          </h1>
          <p class="text-slate-500 font-medium">
            Configure dates, enabled games, currencies with their coin rewards, and classic gameplay tasks.
          </p>
        </div>
      </header>

      <div class="space-y-6 text-left">
        <!-- Section 1: General Settings -->
        <section class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <div class="flex items-center gap-3 mb-6">
            <i class="fa-solid fa-gears text-gray-400 w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"></i>
            <h2 class="font-semibold text-gray-800">General Settings</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
            <!-- Title -->
            <div class="md:col-span-1 space-y-1.5">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Title*</label>
              <p class="text-xs text-slate-400">Identifying name for the loyalty coins campaign</p>
              <input
                v-model="formData.title"
                type="text"
                class="w-full custom-input px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-amber-50 outline-none transition-all focus:border-amber-500 bg-white"
                :class="{ 'border-red-500 bg-red-50': validationErrors.title }"
                placeholder="e.g. Autumn Loyalty Coins Program 2026"
              />
            </div>

            <!-- Target Skin Selector -->
            <div class="md:col-span-1 space-y-1.5">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Target Skin*</label>
              <p class="text-xs text-slate-400">Select target skin or All Skins</p>
              <div class="relative">
                <select
                  v-model="formData.skin_id"
                  class="w-full custom-input px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-amber-50 outline-none transition-all focus:border-amber-500 bg-white appearance-none cursor-pointer text-sm font-medium"
                >
                  <option :value="0">All Skins (Global / 0)</option>
                  <option
                    v-for="skin in allowedSkinsList"
                    :key="skin.ref_id"
                    :value="skin.ref_id"
                  >
                    Skin {{ skin.ref_id }}{{ skin.name ? ' - ' + skin.name : '' }}
                  </option>
                </select>
                <i class="fa-solid fa-chevron-down absolute right-3.5 top-3.5 text-slate-400 pointer-events-none text-xs"></i>
              </div>
            </div>

            <!-- Games Multi-Select Dropdown -->
            <div
              v-if="Object.keys(gameChoose).length > 0"
              class="md:col-span-2 space-y-1.5 relative"
              v-click-outside="() => closeDropdown('game')"
            >
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Choose Games*</label>
              <p class="text-xs text-slate-400">
                Select games where coins accumulation is active. If no games are selected, coins accumulation applies to all provider games.
              </p>

              <div
                @click="toggleDropdown('game')"
                class="custom-input flex items-center justify-between cursor-pointer bg-white min-h-[46px] flex-wrap gap-2 p-2 rounded-xl border border-slate-200"
                :class="{
                  '!border-amber-500 ring-4 ring-amber-500/10': activeDropdown === 'game',
                  'border-red-500': validationErrors.games,
                }"
              >
                <div v-if="formData.games && formData.games.length > 0" class="flex flex-wrap gap-2">
                  <span
                    v-for="id in formData.games"
                    :key="id"
                    class="flex items-center gap-1.5 bg-amber-50 text-amber-800 px-2.5 py-1 rounded-lg text-xs font-semibold border border-amber-200"
                  >
                    {{ gameChoose[id] || id }}
                    <button type="button" @click.stop="removeGame(id)" class="hover:text-amber-950 ml-1">
                      <i class="fa-solid fa-xmark text-xs"></i>
                    </button>
                  </span>
                </div>
                <span v-else class="text-slate-400 text-sm italic">All games enabled (no restrictions)...</span>

                <i
                  class="fa-solid fa-chevron-down text-slate-400 transition-transform ml-auto text-xs"
                  :class="{ 'rotate-180': activeDropdown === 'game' }"
                ></i>
              </div>

              <!-- Game Dropdown Menu -->
              <div
                v-if="activeDropdown === 'game'"
                class="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
              >
                <div class="p-2 border-b border-slate-100 bg-slate-50">
                  <input
                    ref="gameSearchInput"
                    v-model="searchQueries.game"
                    type="text"
                    class="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-amber-500 custom-input bg-white"
                    placeholder="Search game..."
                  />
                </div>
                <ul class="max-h-48 overflow-y-auto custom-scrollbar">
                  <li
                    v-for="(label, value) in filteredGames"
                    :key="value"
                    @click="selectGame(value)"
                    class="px-3.5 py-2.5 hover:bg-amber-50 cursor-pointer flex items-center justify-between text-xs transition-colors border-b border-slate-50 last:border-b-0"
                  >
                    <span :class="formData.games.includes(value) ? 'font-bold text-amber-800' : 'text-slate-700'">
                      {{ label }}
                    </span>
                    <i
                      v-if="formData.games.includes(value)"
                      class="fa-solid fa-check text-amber-600 font-bold"
                    ></i>
                  </li>
                  <li v-if="Object.keys(filteredGames).length === 0" class="px-3 py-4 text-center text-slate-400 text-xs">
                    No games found
                  </li>
                </ul>
              </div>
            </div>

            <!-- Description -->
            <div class="md:col-span-2 space-y-1.5">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Description</label>
              <textarea
                v-model="formData.description"
                rows="2"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-amber-50 outline-none transition-all focus:border-amber-500 resize-none custom-input bg-white text-sm"
                placeholder="Internal campaign description..."
              ></textarea>
            </div>
          </div>
        </section>

        <!-- Section 2: Date & Time Settings -->
        <section class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <div class="flex items-center gap-3 mb-6">
            <i class="fa-solid fa-timeline text-blue-500 w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"></i>
            <h2 class="font-semibold text-gray-800">Date &amp; Time Settings</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Announcement Date</label>
              <p class="text-[10px] text-slate-400">Visible to players but not yet active</p>
              <flat-pickr
                v-model="formData.announcement_date"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-50 outline-none transition-all bg-white text-sm"
                :config="{
                  enableTime: true,
                  time_24hr: true,
                  dateFormat: 'd/m/Y H:i',
                }"
              />
            </div>

            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">Start Date*</label>
              <p class="text-[10px] text-slate-400">Date and time coins accumulation starts</p>
              <flat-pickr
                v-model="formData.start_date"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-50 outline-none transition-all bg-white text-sm"
                :class="{ 'border-red-500': validationErrors.start_date }"
                :config="{
                  enableTime: true,
                  time_24hr: true,
                  dateFormat: 'd/m/Y H:i',
                }"
              />
            </div>

            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-slate-700 uppercase tracking-wider">End Date*</label>
              <p class="text-[10px] text-slate-400">Date and time coins accumulation ends</p>
              <flat-pickr
                v-model="formData.end_date"
                class="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-50 outline-none transition-all bg-white text-sm"
                :class="{ 'border-red-500': validationErrors.end_date }"
                :config="{
                  enableTime: true,
                  time_24hr: true,
                  dateFormat: 'd/m/Y H:i',
                }"
              />
            </div>
          </div>
        </section>

        <!-- Section 3: Configuration & Multi-Currency Coins -->
        <section class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600">
                <i class="fa-solid fa-coins text-lg"></i>
              </div>
              <div>
                <h2 class="font-semibold text-gray-800">Multi-Currency &amp; Coins Configuration</h2>
                <p class="text-xs text-slate-400">Assign coins specific to each supported currency</p>
              </div>
            </div>

            <div class="flex items-center gap-4">
              <!-- Add Currency Button -->
              <button
                @click="changeCurrency('add')"
                type="button"
                class="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-xl font-bold text-xs hover:bg-amber-100 transition-all shadow-sm"
              >
                <i class="fa-solid fa-plus"></i> Add Currency
              </button>
            </div>
          </div>

          <!-- Currencies List -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="(curr, index) in formData.currencies"
              :key="index"
              class="relative p-4 bg-slate-50 rounded-2xl border border-slate-200 group transition-all hover:border-amber-300 hover:bg-white space-y-3"
            >
              <!-- Remove currency button -->
              <button
                v-if="formData.currencies.length > 1"
                @click="changeCurrency('remove', index)"
                type="button"
                class="absolute -top-2 -right-2 w-7 h-7 bg-white border border-rose-200 text-rose-500 rounded-full shadow-sm hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center z-10 text-xs"
              >
                <i class="fa-solid fa-times"></i>
              </button>

              <div class="flex items-center justify-between border-b border-slate-200/60 pb-2">
                <span class="text-xs font-bold uppercase tracking-wider text-slate-500">Currency #{{ index + 1 }}</span>
                <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800">
                  {{ curr.currency_code }}
                </span>
              </div>

              <div class="space-y-3">
                <!-- Currency Code Dropdown -->
                <div class="space-y-1 relative" v-click-outside="() => closeDropdown('curr' + index)">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Currency Code*</label>
                  <div
                    @click="toggleDropdown('curr' + index)"
                    class="w-full px-3 py-2 font-bold rounded-lg border border-slate-200 bg-white flex items-center justify-between text-sm cursor-pointer hover:border-amber-400 transition-colors"
                  >
                    <span class="text-slate-800">{{ curr.currency_code || "Select..." }}</span>
                    <i
                      class="fa-solid fa-chevron-down text-slate-400 transition-transform text-xs"
                      :class="{ 'rotate-180': activeDropdown === 'curr' + index }"
                    ></i>
                  </div>

                  <div
                    v-if="activeDropdown === 'curr' + index"
                    class="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
                  >
                    <div class="p-2 border-b border-slate-100 bg-slate-50">
                      <input
                        v-model="searchQueries.currency"
                        type="text"
                        class="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md outline-none focus:border-amber-500 custom-input bg-white"
                        placeholder="Search currency..."
                      />
                    </div>
                    <ul class="max-h-40 overflow-y-auto custom-scrollbar">
                      <li
                        v-for="opt in filteredCurrencies"
                        :key="opt.code"
                        @click="selectCurrency(index, opt.code)"
                        class="px-3 py-2 hover:bg-amber-50 cursor-pointer flex items-center justify-between text-xs transition-colors"
                      >
                        <span class="font-bold text-slate-800">{{ opt.code }}</span>
                        <span class="text-slate-400 text-[10px]">{{ opt.name }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Coins Awarded -->
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-amber-800 uppercase tracking-widest flex items-center gap-1">
                    <i class="fa-solid fa-coins"></i> Coins Awarded*
                  </label>
                  <input
                    v-model.number="curr.coins"
                    type="number"
                    min="1"
                    class="w-full px-3 py-2 rounded-lg border border-amber-300 bg-amber-50/40 text-amber-900 font-extrabold text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                    placeholder="e.g. 20"
                  />
                  <span class="text-[10px] text-slate-400">Coins awarded on task completion</span>
                </div>

                <!-- Min Bet & Max Bet in this currency -->
                <div class="grid grid-cols-2 gap-2 pt-1">
                  <div class="space-y-1">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Min Bet</label>
                    <input
                      v-model="curr.min_bet"
                      type="text"
                      class="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs outline-none"
                      placeholder="e.g. 0.50"
                    />
                  </div>
                  <div class="space-y-1">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Max Bet</label>
                    <input
                      v-model="curr.max_bet"
                      type="text"
                      class="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs outline-none"
                      placeholder="e.g. 100"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Section 4: Tasks (The classic Challenge tasks!) -->
        <section class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                <i class="fa-solid fa-list-check text-lg"></i>
              </div>
              <div>
                <h2 class="font-semibold text-gray-800">Tasks (Earning Objectives)</h2>
                <p class="text-xs text-slate-400">Classic challenge gameplay tasks that award the loyalty coins configured above</p>
              </div>
            </div>

            <button
              @click="addTask('add')"
              type="button"
              class="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-bold text-xs hover:bg-emerald-100 transition-all shadow-sm"
            >
              ADD TASK <i class="fa-solid fa-plus text-[10px]"></i>
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="(task, index) in formData.tasks"
              :key="index"
              class="relative p-4 bg-slate-50 rounded-2xl border border-slate-200 group transition-all hover:border-emerald-300 hover:bg-white space-y-3"
            >
              <!-- Remove task button -->
              <button
                v-if="formData.tasks.length > 1"
                @click="addTask('remove', index)"
                type="button"
                class="absolute -top-2 -right-2 w-7 h-7 bg-white border border-rose-200 text-rose-500 rounded-full shadow-sm hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center z-10 text-xs"
              >
                <i class="fa-solid fa-times"></i>
              </button>

              <div class="flex items-center justify-between border-b border-slate-200/60 pb-2">
                <span class="text-xs font-bold uppercase tracking-wider text-slate-500">Task #{{ index + 1 }}</span>
                <span class="text-xs font-extrabold text-emerald-700">
                  {{ tasksOptions[task.type] || task.type }}
                </span>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <!-- Task Type -->
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Task Type*</label>
                  <select
                    v-model="task.type"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-800 text-xs font-semibold outline-none focus:border-emerald-500"
                  >
                    <option v-for="(label, val) in tasksOptions" :key="val" :value="val">
                      {{ label }}
                    </option>
                  </select>
                </div>

                <!-- Target Value -->
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    {{ getLabelTask(task.type) }}*
                  </label>
                  <input
                    v-model="task.value"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-800 text-xs font-bold outline-none focus:border-emerald-500"
                    placeholder="e.g. 10"
                    @input="handleNumericInput($event, task, 'value')"
                  />
                </div>

                <!-- Multiplier field (if catch_multipliers) -->
                <div v-if="task.type === 'catch_multipliers'" class="space-y-1 md:col-span-2">
                  <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Minimum Multiplier Target (x)*
                  </label>
                  <input
                    v-model="task.multiplier_reached"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-800 text-xs outline-none focus:border-emerald-500"
                    placeholder="e.g. 5.0"
                    @input="handleNumericInput($event, task, 'multiplier_reached')"
                  />
                </div>
              </div>

              <!-- Task formula explanation -->
              <div class="p-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 flex items-center gap-2">
                <i class="fa-solid fa-circle-info text-emerald-500 text-sm shrink-0"></i>
                <span>
                  {{ getTaskSummary(task) }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- Bottom Action Bar -->
        <div
          class="flex items-center justify-end pt-6 border-t border-gray-200 gap-2"
        >
          <button
            type="button"
            @click="goBack"
            class="px-6 py-2.5 h-auto w-fit rounded-xl bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-all active:scale-95 cursor-pointer"
          >
            {{ $t("util.back") || "Back" }}
          </button>
          <button
            type="button"
            @click="saveLoyaltyCampaign"
            class="text-teal-900 bg-teal-100 inline-flex items-center gap-2 rounded-xl px-4 py-2 transition-colors duration-150 active:scale-95 border border-teal-200 cursor-pointer font-bold"
          >
            <span>{{ $route.params.id ? "UPDATE" : "CREATE" }}</span>
            <i class="fa-solid fa-cloud-bolt"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { postFetch, getFetch, putFetch } from "@/api/axiosClient";
import Loader from "./Loader.vue";
import Swal from "sweetalert2";

const clickOutside = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};

export default {
  name: "LoyaltyCoinsGenerator",
  components: {
    FlatPickr,
    Loader,
  },
  directives: { "click-outside": clickOutside },
  data() {
    return {
      activeDropdown: null,
      searchQueries: { game: "", currency: "" },
      isLoading: false,
      gameChoose: {},
      validationErrors: {},

      formData: {
        title: "",
        description: "",
        note: "",
        mode: "loyalty",
        skin_id: null,
        start_date: null,
        announcement_date: null,
        end_date: null,
        is_enabled: true,
        games: [],
        currencies: [
          {
            currency_code: "EUR",
            coins: 20,
            min_bet: "0.50",
            max_bet: "100.00",
          },
        ],
        tasks: [
          {
            type: "bet_total",
            value: "10",
            min_bet: "0.50",
            max_bet: "100",
            multiplier_reached: "1",
          },
        ],
      },

      tasksOptions: {
        bet_total: "Bet a Total",
        play_rounds: "Play Rounds",
        win_money: "Win Money",
        win_rounds: "Win Rounds",
        catch_multipliers: "Catch Multiplier",
      },

      currenciesOpt: [
        { code: "EUR", name: "Euro" },
        { code: "USD", name: "US Dollar" },
        { code: "BRL", name: "Brazilian Real" },
        { code: "TRY", name: "Turkish Lira" },
        { code: "GBP", name: "British Pound" },
        { code: "USDT", name: "Tether" },
      ],
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen", "skinStorage", "allowedSkins"]),
    allowedSkinsList() {
      if (!Array.isArray(this.allowedSkins)) return [];
      return this.allowedSkins.map((s) => {
        if (typeof s === "object" && s !== null) {
          return {
            ref_id: Number(s.ref_id ?? s.id ?? s.skin_id),
            name: s.name || s.title || "",
          };
        }
        return { ref_id: Number(s), name: "" };
      });
    },
    filteredGames() {
      const q = this.searchQueries.game.toLowerCase();
      if (!q) return this.gameChoose;
      return Object.fromEntries(
        Object.entries(this.gameChoose).filter(([_, label]) =>
          label.toLowerCase().includes(q)
        )
      );
    },
    filteredCurrencies() {
      const q = this.searchQueries.currency.toLowerCase();
      if (!q) return this.currenciesOpt;
      return this.currenciesOpt.filter(
        (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
      );
    },
  },
  async mounted() {
    const params = new URLSearchParams(window.location.search);
    const skinFromQuery = params.get("skin_id");
    const paramSkin = this.$route.params.skinFilt;
    const skinFromUrl = skinFromQuery ?? (paramSkin && paramSkin !== "all" ? paramSkin : null);

    if (skinFromUrl) {
      this.formData.skin_id = parseInt(skinFromUrl, 10) || 0;
    } else if (this.skinStorage) {
      this.formData.skin_id = parseInt(this.skinStorage, 10) || 0;
    } else {
      this.formData.skin_id = 0;
    }

    await this.fetchGames();
    await this.fetchCurrencySuggestions();
    if (this.$route.params.id) {
      await this.fetchCampaignDetails();
    } else {
      this.initDefaultDates();
    }
  },
  methods: {
    formatDateToPicker(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return String(date);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    parsePickerDate(input) {
      if (!input) return null;
      if (input instanceof Date && !isNaN(input.getTime())) return input;
      if (typeof input === "string") {
        input = input.trim();
        if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(input)) {
          const [datePart, timePart = "00:00"] = input.split(" ");
          const [day, month, year] = datePart.split("/").map(Number);
          const [hour = 0, minute = 0] = timePart.split(":").map(Number);
          const dt = new Date(year, month - 1, day, hour, minute);
          return !isNaN(dt.getTime()) ? dt : null;
        }
        const dt = new Date(input);
        return !isNaN(dt.getTime()) ? dt : null;
      }
      return null;
    },

    initDefaultDates() {
      const now = new Date();
      const end = new Date(Date.now() + 30 * 86400000);
      this.formData.start_date = this.formatDateToPicker(now);
      this.formData.end_date = this.formatDateToPicker(end);
    },

    async fetchGames() {
      try {
        const res = await getFetch(`/games`);
        const games = Array.isArray(res) ? res : res?.data || [];
        this.gameChoose = Object.fromEntries(
          games.filter((g) => g.is_enabled).map((g) => [g.game_id, g.name])
        );
      } catch (e) {
        console.error("Fetch games error", e);
        this.gameChoose = {};
      }
    },

    async fetchCurrencySuggestions() {
      try {
        const res = await getFetch("/currencies");
        if (Array.isArray(res) && res.length > 0) {
          this.currenciesOpt = res;
        }
      } catch (e) {
        // fallback to default currencies list
      }
    },

    toggleDropdown(key) {
      if (this.activeDropdown === key) {
        this.activeDropdown = null;
      } else {
        this.activeDropdown = key;
        if (key === "game") {
          this.searchQueries.game = "";
          this.$nextTick(() => this.$refs.gameSearchInput?.focus());
        } else {
          this.searchQueries.currency = "";
        }
      }
    },

    closeDropdown(key) {
      if (this.activeDropdown === key) this.activeDropdown = null;
    },

    selectGame(id) {
      if (!Array.isArray(this.formData.games)) {
        this.formData.games = [];
      }
      if (!this.formData.games.includes(id)) {
        this.formData.games.push(id);
      } else {
        this.removeGame(id);
      }
    },

    removeGame(id) {
      this.formData.games = this.formData.games.filter((gameId) => gameId !== id);
    },

    changeCurrency(type, index = null) {
      if (type === "add") {
        const addedCodes = this.formData.currencies.map((c) => c.currency_code);
        const next = this.currenciesOpt.find((c) => !addedCodes.includes(c.code)) || {
          code: "USD",
          name: "US Dollar",
        };
        this.formData.currencies.push({
          currency_code: next.code,
          coins: 20,
          min_bet: "0.50",
          max_bet: "100.00",
        });
      } else if (type === "remove" && this.formData.currencies.length > 1) {
        this.formData.currencies.splice(index, 1);
      }
    },

    selectCurrency(index, code) {
      this.formData.currencies[index].currency_code = code;
      this.closeDropdown("curr" + index);
    },

    addTask(type, index = null) {
      if (type === "add") {
        this.formData.tasks.push({
          type: "bet_total",
          value: "10",
          min_bet: "0.50",
          max_bet: "100",
          multiplier_reached: "1",
        });
      } else if (type === "remove" && this.formData.tasks.length > 1) {
        this.formData.tasks.splice(index, 1);
      }
    },

    getLabelTask(type) {
      switch (type) {
        case "bet_total":
          return "Total to Bet";
        case "play_rounds":
          return "Rounds to Play";
        case "win_money":
          return "Total to Win";
        case "win_rounds":
          return "Rounds to Win";
        case "catch_multipliers":
          return "Target Value";
        default:
          return "Target Value";
      }
    },

    getTaskSummary(task) {
      const v = task.value || "10";
      switch (task.type) {
        case "bet_total":
          return `Player receives coins each time they bet a total of ${v} in their account currency.`;
        case "play_rounds":
          return `Player receives coins each time they complete ${v} rounds.`;
        case "win_money":
          return `Player receives coins each time they win a total of ${v} in their account currency.`;
        case "win_rounds":
          return `Player receives coins each time they win ${v} rounds.`;
        case "catch_multipliers":
          return `Player receives coins each time they hit a multiplier ≥ ${task.multiplier_reached || "5.0"}x.`;
        default:
          return `Task completed upon reaching ${v}.`;
      }
    },

    handleNumericInput(e, obj, key) {
      let val = e.target.value.replace(/[^0-9.]/g, "");
      const parts = val.split(".");
      if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
      obj[key] = val;
    },

    async fetchCampaignDetails() {
      this.isLoading = true;
      try {
        const data = await getFetch(`/loyalty/${this.$route.params.id}`);
        if (!data) return;

        this.formData = {
          title: data.title || "",
          description: data.description || "",
          note: data.note || "",
          mode: "loyalty",
          skin_id: data.skin_id !== undefined ? Number(data.skin_id) : (this.formData.skin_id || 0),
          start_date: data.start_date ? this.formatDateToPicker(data.start_date) : null,
          end_date: data.end_date ? this.formatDateToPicker(data.end_date) : null,
          announcement_date: data.announcement_date ? this.formatDateToPicker(data.announcement_date) : null,
          is_enabled: !!data.is_enabled,
          games: Array.isArray(data.games) ? data.games : [],
          currencies: Array.isArray(data.currencies) && data.currencies.length > 0
            ? data.currencies.map((c) => ({
                currency_code: c.currency_code || "EUR",
                coins: Number(c.coins) || 20,
                min_bet: c.min_bet !== undefined ? String(c.min_bet) : "0.50",
                max_bet: c.max_bet !== undefined ? String(c.max_bet) : "100.00",
              }))
            : [
                {
                  currency_code: data.currency || "EUR",
                  coins: 20,
                  min_bet: "0.50",
                  max_bet: "100.00",
                },
              ],
          tasks: Array.isArray(data.tasks) && data.tasks.length > 0
            ? data.tasks.map((t) => ({
                type: t.type || "bet_total",
                value: String(t.value || "10"),
                min_bet: String(t.min_bet || "0.50"),
                max_bet: String(t.max_bet || "100"),
                multiplier_reached: String(t.multiplier_reached || "1"),
              }))
            : [
                {
                  type: "bet_total",
                  value: "10",
                  min_bet: "0.50",
                  max_bet: "100",
                  multiplier_reached: "1",
                },
              ],
        };
      } catch (err) {
        console.error("Error loading loyalty campaign details:", err);
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      const lang = this.$route.params.lang || "en";
      const skin = this.formData.skin_id || this.$route.params.skinFilt || this.skinStorage;
      this.$router.push({
        name: "LoyaltyCoins",
        params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
      });
    },

    async saveLoyaltyCampaign() {
      this.validationErrors = {};
      if (!this.formData.title.trim()) {
        this.validationErrors.title = true;
        Swal.fire("Warning", "Please enter a title for the loyalty campaign", "warning");
        return;
      }

      const parsedStart = this.parsePickerDate(this.formData.start_date);
      const parsedEnd = this.parsePickerDate(this.formData.end_date);
      const parsedAnnounce = this.formData.announcement_date ? this.parsePickerDate(this.formData.announcement_date) : null;

      if (!parsedStart) {
        this.validationErrors.start_date = true;
        Swal.fire("Warning", "Please select a valid start date", "warning");
        return;
      }
      if (!parsedEnd) {
        this.validationErrors.end_date = true;
        Swal.fire("Warning", "Please select a valid end date", "warning");
        return;
      }
      if (parsedEnd <= parsedStart) {
        this.validationErrors.end_date = true;
        Swal.fire("Warning", "End date must be after start date", "warning");
        return;
      }
      if (!this.formData.currencies || this.formData.currencies.length === 0) {
        Swal.fire("Warning", "Please configure at least one currency", "warning");
        return;
      }

      this.isLoading = true;
      try {
        const payload = {
          title: this.formData.title,
          description: this.formData.description,
          note: this.formData.note,
          skin_id: parseInt(this.formData.skin_id, 10) || 0,
          start_date: parsedStart.toISOString(),
          end_date: parsedEnd.toISOString(),
          is_enabled: this.formData.is_enabled,
          games: this.formData.games,
          currencies: this.formData.currencies.map((c) => ({
            currency_code: c.currency_code,
            coins: Number(c.coins) || 0,
            min_bet: Number(c.min_bet) || 0,
            max_bet: Number(c.max_bet) || 0,
          })),
          tasks: this.formData.tasks.map((t) => ({
            type: t.type,
            value: Number(t.value) || 0,
            min_bet: Number(t.min_bet) || 0,
            max_bet: Number(t.max_bet) || 0,
            multiplier_reached: Number(t.multiplier_reached) || 1,
          })),
        };

        if (parsedAnnounce) {
          payload.announcement_date = parsedAnnounce.toISOString();
        }

        if (this.$route.params.id) {
          await putFetch(`/loyalty/${this.$route.params.id}`, payload);
          Swal.fire("Success!", "Loyalty Coins campaign updated successfully.", "success");
        } else {
          await postFetch("/loyalty", payload);
          Swal.fire("Success!", "Loyalty Coins campaign created successfully.", "success");
        }

        const targetSkin = this.formData.skin_id || this.$route.params.skinFilt || this.skinStorage;
        this.$router.push({
          name: "LoyaltyCoins",
          params: {
            lang: this.$route.params.lang || "en",
            ...(targetSkin ? { skinFilt: targetSkin.toString() } : {}),
          },
        });
      } catch (err) {
        console.error("Error saving loyalty campaign:", err);
        const errMsg = err?.response?.data?.error || err?.message || "Error saving loyalty campaign";
        Swal.fire("Error", errMsg, "error");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-input {
  @apply w-full px-3 py-2 rounded-xl border border-slate-200 focus:!ring-4 focus:!ring-amber-500/10 focus:!border-amber-500 transition-all outline-none !text-slate-800;
}
</style>
